import React, { JSX } from 'react';
import HeadingWithDetails from 'src/components/HeadingWithDetails';
import Card from 'src/components/Card';
import Tenant from 'src/components/Tenant';
import Avatar from 'src/components/Avatar';
import FontAwesomeIcon from 'src/components/Icon';
import { convertTime } from 'src/helpers/time';
import ServiceRequestLocationBreadcrumb from 'src/pages/ServiceRequest/components/ServiceRequestLocationBreadcrumb/ServiceRequestLocationBreadcrumb';
import './magic-link-sr-details.scss';

interface Props {
  serviceRequest: any;
  ticketNumber?: string | number;
  homeOwnerDataVisible?: boolean | null;
}

const ReporterCard = ({ serviceRequest }: Props) => (
  <Card
    title="Reporter"
    body={
      <Tenant
        headline={serviceRequest.reporter?.name}
        email={serviceRequest.reporter?.email}
        phone={serviceRequest.reporter?.phone}
        strapline={serviceRequest.reporter?.role}
        avatar={
          <Avatar
            url={serviceRequest.reporter?.profileImage}
            badge={undefined}
            size={undefined}
          />
        }
      />
    }
  />
);

const HomeOwnerCard = ({ serviceRequest }: Props) => (
  <Card
    title="Primary Home Owner"
    body={
      <Tenant
        headline={serviceRequest.suite?.homeOwner?.name}
        email={serviceRequest.suite?.homeOwner?.email}
        phone={serviceRequest.suite?.homeOwner?.phone}
        strapline={serviceRequest.suite?.homeOwner?.role}
        avatar={
          <Avatar
            url={serviceRequest.suite?.homeOwner?.profileImage}
            badge={undefined}
            size={undefined}
          />
        }
      />
    }
  />
);

const AssignedToCard = ({ serviceRequest }: Props) => {
  const hasProvider = !!serviceRequest.serviceProvider?.name;

  return (
    <Card
      title="Assigned To"
      body={
        hasProvider ? (
          <Tenant
            headline={serviceRequest.serviceProvider.name}
            strapline={serviceRequest.serviceProvider.role}
            avatar={
              <Avatar
                url={serviceRequest.serviceProvider.profileImage}
                badge="service-provider"
                size={undefined}
              />
            }
          />
        ) : (
          <div>No service provider assigned</div>
        )
      }
    />
  );
};

const AppointmentCard = ({ serviceRequest }: Props) => {
  const isEmpty =
    serviceRequest.appointment?.id === '00000000-0000-0000-0000-000000000000';

  return (
    <Card
      title="Appointment"
      body={
        <div className="serviceRequest__appointment">
          <div className="serviceRequest__appointmentIcon">
            <FontAwesomeIcon glyph="calendar" weight="regular" />
          </div>
          <div>
            {isEmpty
              ? 'No Appointment'
              : convertTime(
                  serviceRequest.appointment?.scheduledAt,
                  'dddd MM/DD/YYYY'
                )}
            <br />
            {!isEmpty &&
              convertTime(serviceRequest.appointment?.scheduledAt, 'h:mm A')}
          </div>
        </div>
      }
    />
  );
};

const MagicLinkServiceRequestDetails: React.FC<Props> = ({ serviceRequest, ticketNumber, homeOwnerDataVisible }) => {
  const reporterId = serviceRequest.reporter?.id;
  const homeOwnerId = serviceRequest.suite?.homeOwner?.id;

  // Offline users are always service providers — gate PII on the module permission flag. null = still loading, treat as hidden until resolved.
  const canSeeHomeOwnerData: boolean = homeOwnerDataVisible === true;
  const reporterIsHomeOwner: boolean = reporterId && reporterId === homeOwnerId;
  const showReporter: boolean = reporterId && (canSeeHomeOwnerData || !reporterIsHomeOwner);
  const showHomeOwner: boolean = canSeeHomeOwnerData && homeOwnerId && homeOwnerId !== reporterId;

  const items: JSX.Element[] = [
    showReporter && (
      <ReporterCard key="reporter" serviceRequest={serviceRequest} />
    ),
    showHomeOwner && (
      <HomeOwnerCard key="homeowner" serviceRequest={serviceRequest} />
    ),
    <AssignedToCard key="assigned" serviceRequest={serviceRequest} />,
    <AppointmentCard key="appointment" serviceRequest={serviceRequest} />,
  ].filter(Boolean);

  return (
    <>
      {ticketNumber && <div className="offline-sr-details-ticket-number">{ticketNumber}</div>}
      <div className="offline-sr-details-filed-in">
        Filed in: <ServiceRequestLocationBreadcrumb serviceRequest={serviceRequest} />
      </div>
      <HeadingWithDetails status={serviceRequest.status?.name} items={items} compact />
    </>
  );
};

export default MagicLinkServiceRequestDetails;
